import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { IconCrossLarge } from '../../../../foundation/icons/IconCrossLarge/index.js';
import { ExpandStates, BottomSheetTypes, TitleAlignments } from '../types/index.js';
import { useStyles } from '../styles/index.js';
import { InlineButton } from '../../InlineButton/InlineButton/index.js';
import { Text } from '../../Text/Text/index.js';
import { TypographyT6 } from '../../../../foundation/typography/typography/index.js';
import { TextColors } from '../../../../foundation/colors/textColors/textColors/index.js';
import { FontWeights } from '../../../../foundation/fonts/fonts/index.js';

const minSwipeDistance = 50;
const transition = 300;
const minimizedHeight = 68;
const BottomSheet = ({ children, className, type = BottomSheetTypes.Static, startIcon: StartIcon, title = 'Title', dataTestId, isOpen = false, onClose, startIconHandler, contentClassName, hideHandleLine, hideHeader, isFullWidth, reverseIcons, titleAlignment = TitleAlignments.Center, zIndex, customHeight = 300, hideOverlay, isSafeAreaExist = true, onExpand, ...props }) => {
    const bottomSheetRef = useRef(null);
    const bottomSheetHeaderRef = useRef(null);
    const [animation, setAnimation] = useState(isOpen);
    const [isSmooth, setIsSmooth] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isDefault, setIsDefault] = useState(isOpen);
    const [startY, setStartY] = useState(null);
    const [transform, setTransform] = useState(null);
    const [viewPortHeight, setViewPortHeight] = useState(window.innerHeight);
    const [headerHeight, setHeaderHeight] = useState(bottomSheetHeaderRef?.current?.clientHeight);
    const [defaultSateHeight, setDefaultSateHeight] = useState(null);
    const [safeArea, setSafeArea] = useState(null);
    const TransformStates = {
        Expanded: viewPortHeight,
        Default: defaultSateHeight,
        Minimized: minimizedHeight,
    };
    const handleExpand = useCallback(() => {
        if (isDefault) {
            onExpand(ExpandStates.Default);
        }
        else if (isExpanded) {
            onExpand(ExpandStates.Full);
        }
        else if (isMinimized) {
            onExpand(ExpandStates.Minimized);
        }
        else {
            onExpand(ExpandStates.Closed);
        }
    }, [isDefault, isExpanded, isMinimized, onExpand]);
    const handleClose = useCallback(() => onClose(), [onClose]);
    const handleResize = () => {
        setViewPortHeight(window.innerHeight);
    };
    useEffect(() => {
        onExpand && handleExpand();
    }, [handleExpand, onExpand]);
    useLayoutEffect(() => {
        setAnimation(isOpen);
        setViewPortHeight(window.innerHeight);
        setSafeArea(getComputedStyle(document.body).getPropertyValue('--sat'));
        if (isOpen) {
            setIsDefault(true);
            setTransform(defaultSateHeight);
        }
        else {
            setIsDefault(false);
            setIsMinimized(false);
        }
        if (type === BottomSheetTypes.Dynamic) {
            setDefaultSateHeight(bottomSheetRef?.current?.clientHeight);
        }
        else if (type === BottomSheetTypes.Static) {
            const height = Number(customHeight);
            setHeaderHeight(bottomSheetHeaderRef?.current?.clientHeight);
            setDefaultSateHeight(height > viewPortHeight ? viewPortHeight : height);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [customHeight, defaultSateHeight, isOpen, safeArea, type, viewPortHeight]);
    const handleTouchStart = e => {
        const touch = e.touches[0];
        setStartY(touch.clientY);
    };
    const handleTouchMove = e => {
        const touch = e.touches[0];
        const deltaY = touch.clientY - startY;
        const isSwipeUp = deltaY < 0;
        const isSwipeDown = deltaY > minSwipeDistance;
        setIsSmooth(false);
        if (viewPortHeight === transform && isSwipeUp) {
            return null;
        }
        if (isMinimized && isSwipeDown && !onClose) {
            return null;
        }
        if (isDefault) {
            const newHeight = Math.max(0, TransformStates.Default - deltaY);
            setTransform(newHeight);
        }
        else if (isMinimized) {
            const newHeight = Math.max(0, TransformStates.Minimized - deltaY);
            setTransform(newHeight);
        }
        else if (isExpanded) {
            const newHeight = Math.max(0, TransformStates.Expanded - (parseInt(safeArea, 10) * 2) - deltaY);
            if (newHeight > viewPortHeight) {
                setTransform(viewPortHeight);
            }
            else {
                setTransform(newHeight);
            }
        }
    };
    const handleTouchEnd = e => {
        const touch = e.changedTouches[0];
        const deltaY = touch.clientY - startY;
        const isSwipeDown = deltaY > minSwipeDistance;
        const isSwipeUp = deltaY < -50;
        setViewPortHeight(window.innerHeight);
        setIsSmooth(true);
        if (!isSwipeUp && !isSwipeDown) {
            if (isDefault) {
                setTransform(TransformStates.Default);
            }
            else if (isMinimized) {
                setTransform(TransformStates.Minimized);
            }
            else if (isExpanded) {
                setTransform(TransformStates.Expanded);
            }
        }
        if (isDefault && isSwipeDown) {
            setIsDefault(false);
            setIsMinimized(true);
            setTransform(TransformStates.Minimized);
        }
        else if (isDefault && isSwipeUp) {
            setIsDefault(false);
            setIsExpanded(true);
            setTransform(TransformStates.Expanded);
        }
        else if (isExpanded && isSwipeDown) {
            setIsExpanded(false);
            setIsDefault(true);
            setTransform(TransformStates.Default);
        }
        else if (isMinimized && isSwipeUp) {
            setIsMinimized(false);
            setIsDefault(true);
            setTransform(TransformStates.Default);
        }
        else if (isMinimized && isSwipeDown) {
            if (onClose) {
                setIsMinimized(false);
                setAnimation(false);
                setTimeout(() => {
                    handleClose();
                    setTransform(TransformStates.Default);
                }, transition);
            }
            else {
                setTransform(TransformStates.Minimized);
            }
        }
    };
    const styles = useStyles({
        animation,
        isFullWidth,
        reverseIcons,
        zIndex,
        hideHeader,
        hideHandleLine,
        titleAlignment,
        startIcon: StartIcon,
        isSmooth,
        transform,
        viewPortHeight,
        type,
        isExpanded,
        headerHeight,
        customHeight,
        hideOverlay,
        isSafeAreaExist,
    });
    const bottomSheet = (jsxs("div", { ...props, className: styles.bottomSheet, "data-testid": dataTestId || 'bottom-sheet', onClick: e => e.stopPropagation(), ref: bottomSheetRef, children: [jsxs("div", { ref: bottomSheetHeaderRef, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, "data-testid": "touch", children: [!hideHandleLine && (jsx("span", { className: styles.swipe })), !hideHeader && (jsxs("div", { className: styles.header, children: [StartIcon && (jsx(InlineButton, { centerIcon: StartIcon, "data-testid": "start-icon", className: clsx(styles.button, styles.actionButton), onClick: startIconHandler, isWithoutBorder: true, isWithoutBackground: true })), title && (jsx(Text, { preventNextLine: true, className: styles.title, weight: FontWeights.semibold, color: TextColors.base, size: TypographyT6, children: title })), jsx(InlineButton, { className: clsx(styles.button, styles.closeButton), onClick: onClose && handleClose, centerIcon: IconCrossLarge, isWithoutBorder: true, isWithoutBackground: true })] }))] }), jsx("div", { className: clsx(styles.content, contentClassName), children: children })] }));
    return createPortal(hideOverlay ? bottomSheet
        : (jsx("div", { className: clsx(styles.root, className), "data-testid": dataTestId || 'bottom-sheet-wrap', onClick: onClose && handleClose, children: bottomSheet })), document.body);
};
BottomSheet.displayName = 'BottomSheet';

export { BottomSheet };
